import React, {useMemo} from "react"
import ShareIcon from "@mui/icons-material/Share"
import {getCondoById} from "@/api/condo"
import copy from "copy-to-clipboard"
import {displayZoneLabel, getShareUrl} from "@/utils"
import {PropertyType, RegionCode, regionTextByCode} from "@casas-diamante/types"

interface SharingProps {
   name: string
   slug: string
   region: RegionCode
   condoId: string
   costs: any
   description: string
   type: PropertyType
   explicitRent: boolean
   zone: string
   capacity: number
   surface: number
   constructionMts: number
   mapsShareUrl: string
   videos: string[]
   className?: string
   accessToBeach: string
}

function formatCurrency(value: number | undefined) {
   return value?.toLocaleString("en-US", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
   })
}

function buildText({
   isRent,
   zone,
   name,
   type,
   region,
   accessToBeach,
   capacity,
   costs,
   description,
   surface,
   constructionMts,
   lastText,
   youtubeUrl,
   mapsURL,
}: {
   isRent: boolean
   zone: string
   name: string
   type: PropertyType
   region: RegionCode
   accessToBeach: string
   capacity: number
   costs: any
   description: string
   surface: number
   constructionMts: number
   lastText: string
   youtubeUrl?: string
   mapsURL?: string
}) {
   let text = `📍 ${regionTextByCode[region]}\n\n`
   text += zone ? `🏘️ ${displayZoneLabel(zone)}\n\n` : ""
   text += `*${name}*\n`
   if (type) text += `🏠 Tipo: ${type}\n\n`
   if (region !== "aca" && accessToBeach) text += `${accessToBeach}\n\n`
   if (isRent && capacity) text += `👨‍👩‍👦‍👦 Capacidad Máxima - ${capacity} personas\n\n`
   if (isRent) {
      if (costs.nightRegular)
         text += `🏷️ *Precio de renta por noche temporada regular ${costs.nightRegular}*\n\n`
      if (costs.nightMid)
         text += `🏷️ *Precio de renta por noche temporada media ${costs.nightMid}*\n\n`
      if (costs.nightHigh)
         text += `🏷️ *Precio de renta por noche temporada alta ${costs.nightHigh}*\n\n`
      if (costs.monthlyRegular)
         text += `🏷️ *Renta por mes en temporada regular ${costs.monthlyRegular}*\n\n`
      if (costs.monthlyAnnual)
         text += `🏷️ *Renta mensual con contrato anual ${costs.monthlyAnnual}*\n\n`
   } else {
      if (costs.salePrice) text += `🏷️ *Precio de venta ${costs.salePrice}*\n\n`
      if (costs.pricePerMeter) text += `🏷️ *Precio por metro cuadrado ${costs.pricePerMeter}*\n\n`
      if (costs.maintenance) text += `🏷️ *Mantenimiento mensual ${costs.maintenance}*\n\n`
   }
   if (description) text += `${description}\n\n`
   if (surface && type !== PropertyType.Apartment)
      text += `📏 Superficie de terreno ${surface.toLocaleString()} Mts²\n`
   if (constructionMts)
      text += `🏠 Superficie de construcción ${constructionMts.toLocaleString()} Mts²\n\n`
   else text += "\n"
   text += lastText
   if (youtubeUrl) text += `\n\n🎥 *Ver video de ${name}*\n${youtubeUrl}`
   if (mapsURL) text += `\n\n📍 *Ver ubicación de ${name}*\n${mapsURL}`
   return text
}

export default function ShareBtn(props: SharingProps) {
   const {
      name,
      costs,
      condoId,
      description,
      explicitRent,
      zone,
      capacity,
      surface,
      constructionMts,
      type,
      videos,
      mapsShareUrl,
      className,
      slug,
      accessToBeach,
      region,
   } = props

   const parsedRentCosts = useMemo(
      () => ({
         nightRegular: formatCurrency(costs?.["noche_regular"]),
         nightMid: formatCurrency(costs?.["noche_media"]),
         nightHigh: formatCurrency(costs?.["noche_alta"]),
         monthlyRegular: formatCurrency(costs?.["mensual_regular"]),
         monthlyAnnual: formatCurrency(costs?.["mensual_anual"]),
      }),
      [costs],
   )

   const parsedSaleCosts = useMemo(
      () => ({
         pricePerMeter: formatCurrency(costs?.pricePerMeter),
         salePrice: formatCurrency(costs?.salePrice),
         maintenance: formatCurrency(costs?.maintenance),
      }),
      [costs],
   )

   const isSale = parsedSaleCosts.salePrice !== undefined
   const forRent = explicitRent || !isSale
   const scheme = forRent ? "rent" : "sale"
   const urlPath = getShareUrl(region, type, scheme, slug)
   const lastText = `ℹ️ *Ver información de ${name}*\n${urlPath}`
   const youtubeUrl = videos?.find((v: string) => v.includes("youtube"))

   const buttonClass = `drop-shadow-lg fixed bottom-8 right-8 rounded-full p-3 bg-blue-600 z-50 ${
      className ?? ""
   }`

   const handleCopy = async (e: React.MouseEvent) => {
      e.stopPropagation()
      let mapsURL = mapsShareUrl
      if (!mapsURL) {
         const condo = await getCondoById(condoId)
         mapsURL = condo?.mapsShareUrl
      }
      const text = buildText({
         isRent: forRent,
         zone,
         name,
         type,
         region,
         accessToBeach,
         capacity,
         costs: forRent ? parsedRentCosts : parsedSaleCosts,
         description,
         surface,
         constructionMts,
         lastText,
         youtubeUrl,
         mapsURL,
      })
      copy(text)
      alert(`¡Información de ${name} copiada al portapapeles!
Simplemente pega en en tu red social favorita o en donde quieras compartir.`)
   }

   return (
      <button className={buttonClass} onClick={handleCopy}>
         <ShareIcon style={{color: "white"}} />
      </button>
   )
}
